import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Swal from "sweetalert2";
import AccessUiMessageModal from "../Components/AccessUiMessageModal";
import {
  deleteAdminAccessUiMessage,
  getAdminAccessUiMessages,
  upsertAdminAccessUiMessage,
  type AccessUiMessageRecord,
} from "../api/adminAccessUiApi";
import { usePermissionCheck } from "../hooks/usePermissionCheck";

const BASE_TEMPLATE: Omit<AccessUiMessageRecord, "reason"> = {
  title: "Bölüm kilitli",
  message: "Bu bölüm şu anda okunamıyor.",
  primaryCtaLabel: null,
  primaryCtaUrl: null,
  secondaryCtaLabel: null,
  secondaryCtaUrl: null,
  showSingleUnlock: false,
  showPackages: true,
  showLoginPrompt: false,
};

const REASON_HINTS: Record<string, string> = {
  need_login: "Kullanıcı oturum açmamışsa gösterilir.",
  need_package: "Aktif paketi olmayan kullanıcı premium bölüm açmaya çalışırsa.",
  quota_exhausted: "Premium paket sahibi günlük hakkını tükettiğinde.",
  free_quota_exhausted: "Ücretsiz kotası kalmadığında.",
  can_single_unlock: "Kullanıcı tekli açma seçeneğine yönlendirilecekse.",
  pending_review: "Bölüm moderasyon veya yayına hazırlık aşamasındaysa.",
  account_banned: "Hesabı kısıtlanan kullanıcılar için.",
  content_missing: "Bölüm yayından kaldırıldıysa veya henüz hazır değilse.",
  http_403: "403 hata kodu alan tüm istekler için.",
  http_404: "404 hata kodu alan tüm istekler için.",
  http_402: "402 - ödeme gerekli hataları için.",
  generic: "Varsayılan uyarı mesajı.",
};

const SUGGESTED_REASON_PRESETS: AccessUiMessageRecord[] = [
  buildPreset({
    reason: "need_package",
    title: "Premium paket gerekli",
    message: "Bu bölüm premium. Paketin yoksa tekli açabilir veya paket satın alabilirsin.",
    primaryCtaLabel: "Paketleri incele",
    primaryCtaUrl: "/packages",
    secondaryCtaLabel: "Tekli aç",
    secondaryCtaUrl: "/packages#single-unlock",
    showPackages: true,
    showSingleUnlock: true,
    showLoginPrompt: true,
  }),
  buildPreset({
    reason: "need_login",
    title: "Giriş yapmalısın",
    message: "Haklarının tanımlanması için hesabına giriş yap.",
    primaryCtaLabel: "Giriş yap",
    primaryCtaUrl: "/login",
    showPackages: false,
    showLoginPrompt: true,
  }),
  buildPreset({
    reason: "quota_exhausted",
    title: "Paket hakkın doldu",
    message: "Aktif paketindeki günlük premium hakkını kullandın. Paketi yükseltebilir veya tekli açabilirsin.",
    primaryCtaLabel: "Paketi yükselt",
    primaryCtaUrl: "/packages",
    secondaryCtaLabel: "Tekli aç",
    secondaryCtaUrl: "/packages#single-unlock",
    showPackages: true,
    showSingleUnlock: true,
  }),
  buildPreset({
    reason: "free_quota_exhausted",
    title: "Ücretsiz hakkın kalmadı",
    message: "Ücretsiz günlük kontenjanını bitirdin. Premium pakete geçebilir ya da tekli açabilirsin.",
    primaryCtaLabel: "Premium paketler",
    primaryCtaUrl: "/packages",
    secondaryCtaLabel: "Z token ile aç",
    secondaryCtaUrl: "/packages#single-unlock",
    showPackages: true,
    showSingleUnlock: true,
  }),
  buildPreset({
    reason: "pending_review",
    title: "Bölüm yayına alınmadı",
    message: "Bu bölüm moderasyon sürecinde. Onaylandığında seni haberdar edeceğiz.",
    showPackages: false,
  }),
  buildPreset({
    reason: "account_banned",
    title: "Hesabın kısıtlı",
    message: "Ekibimizle iletişime geçmeden bu bölüm açılamaz.",
    showPackages: false,
  }),
  buildPreset({
    reason: "http_403",
    title: "Yetkin yok",
    message: "Bu işlemi gerçekleştirmek için gerekli iznin bulunmuyor.",
    primaryCtaLabel: "Destek ile iletişime geç",
    primaryCtaUrl: "/support",
    showPackages: false,
  }),
  buildPreset({
    reason: "http_404",
    title: "İçerik bulunamadı",
    message: "Aradığın içerik kaldırılmış veya hiç oluşturulmamış olabilir.",
    secondaryCtaLabel: "Ana sayfaya dön",
    secondaryCtaUrl: "/",
    showPackages: false,
  }),
  buildPreset({
    reason: "http_402",
    title: "Ödeme gerekli",
    message: "Bu içeriğe erişebilmek için ilgili paketi veya tekli açmayı satın almalısın.",
    primaryCtaLabel: "Paketleri incele",
    primaryCtaUrl: "/packages",
    showPackages: true,
    showSingleUnlock: true,
  }),
  buildPreset({
    reason: "content_missing",
    title: "Bölüm bulunamadı",
    message: "Bölüm kaldırılmış veya henüz hazır değil. Seri sayfasından diğer bölümlere göz atabilirsin.",
    secondaryCtaLabel: "Seri sayfası",
    secondaryCtaUrl: "/content",
    showPackages: false,
  }),
  buildPreset({
    reason: "generic",
    title: "Bölüm kilitli",
    message: "Bu bölüm şu anda okunamıyor. Lütfen daha sonra tekrar dene.",
    showPackages: true,
  }),
];

function buildPreset(overrides: Partial<AccessUiMessageRecord> & { reason: string }): AccessUiMessageRecord {
  return {
    ...BASE_TEMPLATE,
    ...overrides,
    reason: overrides.reason,
    primaryCtaLabel: overrides.primaryCtaLabel ?? BASE_TEMPLATE.primaryCtaLabel ?? null,
    primaryCtaUrl: overrides.primaryCtaUrl ?? BASE_TEMPLATE.primaryCtaUrl ?? null,
    secondaryCtaLabel: overrides.secondaryCtaLabel ?? BASE_TEMPLATE.secondaryCtaLabel ?? null,
    secondaryCtaUrl: overrides.secondaryCtaUrl ?? BASE_TEMPLATE.secondaryCtaUrl ?? null,
    showSingleUnlock: overrides.showSingleUnlock ?? BASE_TEMPLATE.showSingleUnlock,
    showPackages: overrides.showPackages ?? BASE_TEMPLATE.showPackages,
    showLoginPrompt: overrides.showLoginPrompt ?? BASE_TEMPLATE.showLoginPrompt,
    updatedAtUtc: overrides.updatedAtUtc,
  };
}

export default function AdminAccessUi() {
  const { loading: checkingPermission, allowed } = usePermissionCheck("Economy.View");
  const { allowed: canEdit } = usePermissionCheck("Economy.Settings");
  const queryClient = useQueryClient();
  const [customReason, setCustomReason] = useState("");
  const [deletingReason, setDeletingReason] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalDraft, setModalDraft] = useState<AccessUiMessageRecord | null>(null);
  const [modalSaving, setModalSaving] = useState(false);
  const [allowReasonEdit, setAllowReasonEdit] = useState(false);

  const messagesQuery = useQuery({
    queryKey: ["admin-access-ui-messages"],
    enabled: allowed,
    staleTime: 30_000,
    queryFn: async () => sortByReason(await getAdminAccessUiMessages()),
  });

  const upsertMutation = useMutation({
    mutationFn: upsertAdminAccessUiMessage,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteAdminAccessUiMessage,
  });

  const drafts = useMemo(() => messagesQuery.data ?? [], [messagesQuery.data]);
  const loading = allowed ? messagesQuery.isLoading : false;

  const existingReasons = useMemo(() => new Set(drafts.map((draft) => draft.reason)), [drafts]);

  const refreshList = async () => {
    await queryClient.invalidateQueries({ queryKey: ["admin-access-ui-messages"] });
  };

  const openModal = (record: AccessUiMessageRecord, allowEdit = false) => {
    setModalDraft(record);
    setAllowReasonEdit(allowEdit || !record.id);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalDraft(null);
    setAllowReasonEdit(false);
  };

  const handlePresetClick = (reason: string) => {
    const existing = drafts.find((item) => item.reason === reason);
    if (existing) {
      openModal(existing, false);
      return;
    }
    const preset = SUGGESTED_REASON_PRESETS.find((item) => item.reason === reason);
    openModal(preset ?? buildPreset({ reason, title: "Yeni sebep", message: "Bu sebep için mesajı özelleştirin." }), false);
  };

  const handleAddCustomReason = () => {
    const normalized = normalizeReason(customReason);
    if (!normalized) {
      Swal.fire("Geçersiz sebep", "Lütfen harf ve rakamlardan oluşan bir reason girin.", "warning");
      return;
    }

    if (existingReasons.has(normalized)) {
      const existing = drafts.find((item) => item.reason === normalized);
      if (existing) {
        openModal(existing, false);
        setCustomReason("");
        return;
      }
    }

    const draft = buildPreset({
      reason: normalized,
      title: "Yeni sebep",
      message: "Bu sebep için özel mesaj girin.",
    });
    openModal(draft, true);
    setCustomReason("");
  };

  const handleDelete = async (reason: string) => {
    const draft = drafts.find((item) => item.reason === reason);
    if (!draft) {
      return;
    }

    const confirmation = await Swal.fire({
      title: "Mesaj silinsin mi?",
      text: `"${reason}" sebebine ait mesajı kalıcı olarak kaldırmak üzeresiniz.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Evet, sil",
      cancelButtonText: "Vazgeç",
    });

    if (!confirmation.isConfirmed) {
      return;
    }

    if (!draft.id) {
      await refreshList();
      return;
    }

    setDeletingReason(reason);
    const result = await deleteMutation.mutateAsync(reason);
    setDeletingReason(null);
    if (result.success) {
      await refreshList();
      Swal.fire("Silindi", `${reason} mesajı kaldırıldı.`, "success");
    } else {
      Swal.fire("Hata", "Mesaj silinirken bir sorun oluştu.", "error");
    }
  };

  const handleModalSave = async (payload: AccessUiMessageRecord) => {
    if (!canEdit) {
      Swal.fire("Yetki yok", "Bu alanı güncelleme iznin bulunmuyor.", "warning");
      return;
    }

    const normalizedReason = payload.id ? payload.reason : normalizeReason(payload.reason);
    if (!normalizedReason) {
      Swal.fire("Geçersiz reason", "Reason alanı boş bırakılamaz.", "warning");
      return;
    }

    setModalSaving(true);
    const result = await upsertMutation.mutateAsync(
      sanitizeDraftForSave({
        ...payload,
        reason: normalizedReason,
      }),
    );
    setModalSaving(false);

    if (result.success) {
      Swal.fire("Kaydedildi", `${normalizedReason} mesajı güncellendi.`, "success");
      closeModal();
      await refreshList();
    } else {
      Swal.fire("Hata", "Mesaj kaydedilirken bir sorun oluştu.", "error");
    }
  };

  if (checkingPermission) {
    return <div className="rounded-lg bg-white p-6 text-center text-sm text-slate-500 shadow">Yetkiler kontrol ediliyor...</div>;
  }

  if (!allowed) {
    return <div className="rounded-lg bg-white p-6 text-center text-sm text-red-500 shadow">Bu sayfayı görüntüleme yetkin yok.</div>;
  }

  return (
    <div className="space-y-6">
      <header className="rounded-3xl bg-white p-6 shadow">
        <h1 className="text-xl font-semibold text-slate-900">Access UI Mesajları</h1>
        <p className="mt-2 text-sm text-slate-500">
          Premium, paket veya hata durumlarında kullanıcıya göstereceğimiz kartları buradan düzenleyebilirsin. Her reason için başlık, mesaj, CTA ve
          buton seçeneklerini özelleştir.
        </p>
      </header>

      <section className="rounded-3xl bg-white p-5 shadow">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="text-sm font-semibold text-slate-800">Önerilen reason listesi</div>
            <p className="text-xs text-slate-500">403/404 gibi ortak durumlar için hızlıca kart şablonu aç.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {SUGGESTED_REASON_PRESETS.map((preset) => {
              const exists = existingReasons.has(preset.reason);
              return (
                <button
                  key={preset.reason}
                  type="button"
                  onClick={() => handlePresetClick(preset.reason)}
                  className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                    exists
                      ? "border-slate-200 bg-slate-100 text-slate-500 hover:bg-slate-200"
                      : "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                  }`}
                >
                  {preset.reason}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-6 rounded-2xl border border-slate-100 bg-slate-50/70 p-4">
          <div className="text-sm font-semibold text-slate-800">Özel reason oluştur</div>
          <p className="text-xs text-slate-500">Örn. maintenance_mode, geo_lock veya backend'de dönecek özel bir reason kodu.</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <input
              type="text"
              value={customReason}
              onChange={(event) => setCustomReason(event.target.value)}
              placeholder="örn. maintenance_mode"
              className="w-full flex-1 rounded border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none md:w-auto"
            />
            <button
              type="button"
              onClick={handleAddCustomReason}
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
            >
              Modal aç
            </button>
          </div>
        </div>
      </section>

      {loading ? (
        <div className="rounded-3xl bg-white p-6 text-center text-sm text-slate-500 shadow">Mesajlar yükleniyor...</div>
      ) : drafts.length === 0 ? (
        <div className="rounded-3xl bg-white p-6 text-center text-sm text-slate-500 shadow">
          Henüz kayıtlı mesaj yok. Yukarıdaki önerilen reason butonlarından birini seçerek hızlıca oluşturabilirsin.
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {drafts.map((draft) => (
            <article key={draft.reason} className="flex h-full flex-col rounded-3xl border border-white/10 bg-white p-5 shadow">
              <header className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{draft.reason}</p>
                  <p className="text-xs text-slate-500">{REASON_HINTS[draft.reason] ?? "Özel reason"}</p>
                  {draft.updatedAtUtc && (
                    <p className="mt-1 text-[11px] text-slate-400">Güncellendi: {new Date(draft.updatedAtUtc).toLocaleString("tr-TR")}</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => openModal(draft, false)}
                    className="rounded-lg border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Düzenle
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(draft.reason)}
                    disabled={deletingReason === draft.reason}
                    className="rounded-lg border border-red-200 px-3 py-1 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:border-slate-200 disabled:text-slate-400"
                  >
                    Sil
                  </button>
                </div>
              </header>

              <div className="mt-5 space-y-2 text-sm">
                <p className="text-slate-900 font-semibold">{draft.title}</p>
                <p className="text-slate-600">{draft.message}</p>
              </div>

              <div className="mt-4 space-y-2 text-xs text-slate-500">
                {draft.primaryCtaLabel && (
                  <div className="rounded-2xl border border-emerald-100 bg-emerald-50 px-3 py-2 text-emerald-800">
                    Birincil CTA: <span className="font-semibold">{draft.primaryCtaLabel}</span>{" "}
                    {draft.primaryCtaUrl && <span className="text-emerald-600">({draft.primaryCtaUrl})</span>}
                  </div>
                )}
                {draft.secondaryCtaLabel && (
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-slate-700">
                    İkincil CTA: <span className="font-semibold">{draft.secondaryCtaLabel}</span>{" "}
                    {draft.secondaryCtaUrl && <span className="text-slate-500">({draft.secondaryCtaUrl})</span>}
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  {renderToggleBadge("Paket CTA", draft.showPackages)}
                  {renderToggleBadge("Tekli açma", draft.showSingleUnlock)}
                  {renderToggleBadge("Giriş çağrısı", draft.showLoginPrompt)}
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      <AccessUiMessageModal
        open={modalOpen}
        initial={modalDraft}
        onClose={closeModal}
        onSave={handleModalSave}
        saving={modalSaving}
        allowReasonEdit={allowReasonEdit}
      />
    </div>
  );
}

function renderToggleBadge(label: string, active?: boolean) {
  return (
    <span
      className={`rounded-full px-3 py-1 text-[11px] font-semibold ${
        active ? "bg-emerald-50 text-emerald-700 border border-emerald-100" : "bg-slate-100 text-slate-500 border border-slate-200"
      }`}
    >
      {label}: {active ? "Açık" : "Kapalı"}
    </span>
  );
}

function normalizeReason(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function sortByReason(list: AccessUiMessageRecord[]): AccessUiMessageRecord[] {
  return [...list].sort((a, b) => a.reason.localeCompare(b.reason));
}

function sanitizeDraftForSave(draft: AccessUiMessageRecord): AccessUiMessageRecord {
  const clean = (value?: string | null) => {
    if (typeof value !== "string") {
      return null;
    }
    const trimmed = value.trim();
    return trimmed.length ? trimmed : null;
  };

  return {
    ...draft,
    primaryCtaLabel: clean(draft.primaryCtaLabel),
    primaryCtaUrl: clean(draft.primaryCtaUrl),
    secondaryCtaLabel: clean(draft.secondaryCtaLabel),
    secondaryCtaUrl: clean(draft.secondaryCtaUrl),
  };
}
